import React, { FC, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import Collapsible from 'react-collapsible';
import Icon, { Icons } from '@components/Icon';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { batch, useDispatch, useSelector } from 'react-redux';
import { BasketScrappageDetailsProps } from '@components/v2/BasketScrappageDetails/index';
import { useGetNotEscapedTranslationMarkup } from '@hooks/useGetNotEscapedTranslationMarkup';
import BasketSelectSection from '@components/v2/BasketSelectSection';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import Spinner from '@components/Spinner';
import { theme } from '../../../styles/theme';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { Link } from '@components/v2/link';
import { BASKET_STEPS, ModalVersion, SCRAPPAGE_VALUES } from '../../../constants/main';
import { DealService } from '../../../services';
import { getSessionIdCookie } from '@utils/Session.utils';
import carDetailsDuck from '../../../redux/carDetails/carDetails.duck';
import FinanceWidgetDuck from 'src/redux/financeWidget/financeWidget.duck';
import GlobalDuck from 'src/redux/global/global.duck';

const BasketScrappageDetailsTemplate: FC<BasketScrappageDetailsProps> = ({ className, generatedIndex }) => {
    const { t, hasValue } = useTranslations();
    const dispatch = useDispatch();
    const { carDetails, currentDeal } = useCarDetailsDuck();
    const { formatMoney } = useMoneyFormatter();

    const [isLoading, setIsLoading] = useState(false);

    const { getNotEscapedTranslationMarkup } = useGetNotEscapedTranslationMarkup();

    const [isOpen, setIsOpen] = useState(true);

    const openModal = () => {
        dispatch(
            UIDuck.openModal({
                data: { onlyInfo: true },
                callbacks: {},
                modalType: UIDuck.MODAL_TYPES.SCRAPPAGE,
                modalVersion: ModalVersion.v2,
            })
        );
    };

    const selectScrappageChoice = async (scrappageValue: string) => {
        setIsLoading(true);

        const { data } = await DealService.applyScrappage(
            scrappageValue === SCRAPPAGE_VALUES.YES,
            getSessionIdCookie()
        );

        batch(() => {
            dispatch(carDetailsDuck.setCurrentDeal(data.deal));
            dispatch(carDetailsDuck.setCurrentCarDetails(data.deal.fullProductConfiguration));
            if (data.financeQuote?.token) {
                dispatch(FinanceWidgetDuck.setFinanceQuote(data.financeQuote));
            }
            dispatch(GlobalDuck.setIsWithScrappageDone(true));
        });

        setIsLoading(false);
    };

    const brandEcoBonus = carDetails.importInputs.brandEcologicalBonus;
    const ecoBonusWithScrappage = carDetails.importInputs.isWithScrappage
        ? carDetails.importInputs.ecologicalBonusScrappage
        : 0;
    const ecoBonusWithoutScrappage = carDetails.importInputs.ecologicalBonus - ecoBonusWithScrappage;

    return (
        <div id="scrappage" data-testid="scrappage" className={className}>
            <Collapsible
                lazyRender={true}
                open={isOpen}
                trigger={
                    <AccessibleButton className="titleWrapperButton" onClick={() => setIsOpen(!isOpen)}>
                        <div className="titleWrapper">
                            <div className="titleContainer">
                                <span className="sectionNumber">{generatedIndex}.</span>
                                <span className="title">{`${t('basket.scrappage.title')}`}</span>
                            </div>
                            <Icon
                                className={isOpen ? 'isOpen' : 'isClose'}
                                name={isOpen ? Icons.ChevronUp : Icons.ChevronDown}
                            />
                        </div>
                    </AccessibleButton>
                }
            >
                <div className="description">
                    <span dangerouslySetInnerHTML={getNotEscapedTranslationMarkup('basket.scrappage.description')} />
                    <Link
                        withParent={false}
                        isUnderline
                        txt={t('basket.scrappage.description.link')}
                        onClick={openModal}
                    >
                        <button className="link" onClick={openModal}>
                            {t('basket.scrappage.description.link')}
                        </button>
                    </Link>
                </div>
                <BasketSelectSection
                    leftSectionTitle={t('basket.scrappage.with.title')}
                    leftSectionDescription={t('basket.scrappage.with.description')}
                    rightSectionTitle={t('basket.scrappage.without.title')}
                    rightSectionDescription={t('basket.scrappage.without.description')}
                    leftValue={SCRAPPAGE_VALUES.YES}
                    rightValue={SCRAPPAGE_VALUES.NO}
                    leftSelected={carDetails.importInputs.isWithScrappage}
                    rightSelected={!carDetails.importInputs.isWithScrappage}
                    onClick={selectScrappageChoice}
                />
                {isLoading && <Spinner size={25} border={3} color={theme.colors.black} />}
                {!isLoading && (
                    <div className="summary">
                        <div className="leftSection">
                            <p
                                dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                                    'basket.scrappage.description1'
                                )}
                            />
                            {hasValue('basket.scrappage.description2') && (
                                <p
                                    dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                                        'basket.scrappage.description2'
                                    )}
                                />
                            )}
                        </div>
                        <div className="rightSection">
                            <p>
                                <span>{t('basket.scrappage.ecoBonus.value')}</span>
                                <span className="price">
                                    {formatMoney(ecoBonusWithoutScrappage + brandEcoBonus, true)}
                                </span>
                            </p>
                            {carDetails.importInputs.isWithScrappage && (
                                <p data-testid="scrappageBonusValue">
                                    <span>{t('basket.scrappage.scrappage.value')}</span>
                                    <span className="price">{formatMoney(ecoBonusWithScrappage, true)}</span>
                                </p>
                            )}
                            <p className="totalPrice">
                                <span>{t('basket.scrappage.total.value')}</span>
                                <span className="price">
                                    {formatMoney(
                                        ecoBonusWithScrappage + ecoBonusWithoutScrappage + brandEcoBonus,
                                        true
                                    )}
                                </span>
                            </p>
                        </div>
                    </div>
                )}
            </Collapsible>
        </div>
    );
};

export default BasketScrappageDetailsTemplate;
